import { EntityType } from '@frontend/common';
import { Feature } from '@frontend/functionality/types';
import { SettingClient } from '@frontend/setting/api';
import type { Setting } from '@frontend/setting/types';
import { updateFunctionalityContext } from '@frontend/wizard/mobile';

import { useLicenseRepository } from '../../utils/license-repository';
import { useScreen } from '../screens/useScreen';
import { useWizardTransaction } from './useWizardTransaction';

interface UseOpenDropOffReturn {
    isOpenDropOffEnabled: boolean;
    enterOpenDropOff: () => Promise<Setting[] | null>;
}

/**
 * Shared open drop-off entry: resolve functionality, load settings into wizard context, set feature.
 */
export const useOpenDropOff = (): UseOpenDropOffReturn => {
    const { store, dispatch } = useScreen();
    const { functionalities } = useLicenseRepository();
    const { localUpdateTransaction } = useWizardTransaction({
        wizardId: store?.wizard.id,
        store,
        autoCreate: false,
        autoFetch: false
    });

    const openDropOffFunctionality = functionalities.find(
        (func) => func.feature === Feature.OPEN_DROP_OFF && func.application === store?.functionality.application
    );

    const enterOpenDropOff = async (): Promise<Setting[] | null> => {
        if (!store || !openDropOffFunctionality) return null;

        const settingsRes = await SettingClient.fetchSettings({
            entity_type: EntityType.FUNCTIONALITY,
            entity_id: openDropOffFunctionality.id
        });

        dispatch(
            updateFunctionalityContext({
                key: store.wizard.id,
                functionality: openDropOffFunctionality,
                functionalitySettings: settingsRes.results
            })
        );

        await localUpdateTransaction({ feature: Feature.OPEN_DROP_OFF });
        return settingsRes.results;
    };

    return {
        isOpenDropOffEnabled: !!openDropOffFunctionality,
        enterOpenDropOff
    };
};
